/**
 * ATENCION PAGE — Vista de atención activa de toma de muestras.
 *
 * Flujo serial obligatorio: Encuesta → Validación → Muestras
 * Cada paso solo se habilita cuando el anterior está completo.
 *
 * Requirements: M4.15.1, M4.15.7
 */

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft,
  ClipboardList,
  FlaskConical,
  CheckCircle,
  XCircle,
  Printer,
  Lock,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { useApiError } from "@/hooks/use-api-error";
import {
  useAtencionDetalle,
  useValidarExamenes,
  useGenerarMuestras,
  useCerrarAtencion,
  useGetEtiquetasAtencion,
} from "@/hooks/use-muestras";

import { PacienteHeader } from "./components/paciente-header";
import { EncuestaPreanaliticaPanel } from "./components/encuesta-preanalitica-panel";
import { ValidacionPanel } from "./components/validacion-panel";
import { MuestrasPanel } from "./components/muestras-panel";
import { CierreAtencionDialog } from "./components/cierre-atencion-dialog";
import { TiempoAtencionTimer } from "./components/tiempo-atencion-timer";

type TabActivo = "encuesta" | "validacion" | "muestras";

export default function AtencionPage() {
  const { id } = useParams<{ id: string }>();
  const atencionId = Number(id);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getErrorMessage } = useApiError();

  const [tabActivo, setTabActivo] = useState<TabActivo>("encuesta");
  const [isCierreDialogOpen, setIsCierreDialogOpen] = useState(false);

  const { data: atencion, isLoading, error } = useAtencionDetalle(atencionId);

  const validarMutation = useValidarExamenes(atencionId, {
    onSuccess: () => {
      toast.success(t("muestras.validacion.completada"));
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const generarMuestrasMutation = useGenerarMuestras(atencionId, {
    onSuccess: () => toast.success(t("muestras.muestras.generadas")),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const cerrarMutation = useCerrarAtencion(atencionId, {
    onSuccess: () => {
      toast.success(t("muestras.cierre.exitoso"));
      navigate("/muestras");
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const etiquetasMutation = useGetEtiquetasAtencion({
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !atencion) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <XCircle className="h-4 w-4 shrink-0" />
          <span>
            {error ? getErrorMessage(error as Error) : t("muestras.atencion.no_encontrada")}
          </span>
        </div>
      </div>
    );
  }

  // ── Condiciones de desbloqueo de cada paso ────────────────────────────────
  // Encuesta completa: condicion_actual existe Y tiene síntomas
  const encuestaCompleta =
    !!atencion.condicion_actual &&
    (atencion.condicion_actual.sintomas_actuales ?? "").trim().length > 0;

  // Validación completa: hay al menos una validación registrada
  const validacionCompleta = (atencion.validaciones ?? []).length > 0;

  const estaAbierta = atencion.estado === "ABIERTA";

  // Navegar a tab solo si está desbloqueada
  const handleTabChange = (tab: string) => {
    if (tab === "validacion" && !encuestaCompleta) return;
    if (tab === "muestras" && !validacionCompleta) return;
    setTabActivo(tab as TabActivo);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Encabezado clínico fijo */}
      <div className="border-b bg-background sticky top-0 z-10">
        <div className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate("/muestras")}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t("muestras.cola.titulo")}
            </Button>
            <div className="flex items-center gap-2">
              {atencion.muestras && atencion.muestras.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => etiquetasMutation.mutate(atencionId)}
                  disabled={etiquetasMutation.isPending}
                >
                  <Printer className="h-4 w-4 mr-1" />
                  {etiquetasMutation.isPending
                    ? t("common.loading")
                    : t("muestras.etiquetas.imprimir_todas")}
                </Button>
              )}
              <TiempoAtencionTimer horaInicio={atencion.hora_inicio} />
            </div>
          </div>
          <PacienteHeader atencion={atencion} />
        </div>
      </div>

      {/* Contenido por pasos */}
      <div className="flex-1 p-4 overflow-auto">
        <Tabs value={tabActivo} onValueChange={handleTabChange}>
          <TabsList className="mb-4">
            {/* Tab 1: Encuesta — siempre disponible */}
            <TabsTrigger value="encuesta" className="flex items-center gap-1">
              <ClipboardList className="h-4 w-4" />
              {t("muestras.encuesta.titulo")}
              {encuestaCompleta && <CheckCircle className="h-3.5 w-3.5 text-green-500 ml-0.5" />}
            </TabsTrigger>

            {/* Tab 2: Validación — bloqueada hasta completar encuesta */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <TabsTrigger
                      value="validacion"
                      disabled={!encuestaCompleta}
                      className="flex items-center gap-1 data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed"
                    >
                      {!encuestaCompleta ? (
                        <Lock className="h-3.5 w-3.5" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                      {t("muestras.validacion.titulo")}
                      {validacionCompleta && (
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 ml-0.5" />
                      )}
                    </TabsTrigger>
                  </span>
                </TooltipTrigger>
                {!encuestaCompleta && (
                  <TooltipContent>
                    <p>{t("muestras.atencion.completar_encuesta_primero")}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            {/* Tab 3: Muestras — bloqueada hasta completar validación */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <TabsTrigger
                      value="muestras"
                      disabled={!validacionCompleta}
                      className="flex items-center gap-1 data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed"
                    >
                      {!validacionCompleta ? (
                        <Lock className="h-3.5 w-3.5" />
                      ) : (
                        <FlaskConical className="h-4 w-4" />
                      )}
                      {t("muestras.muestras.titulo")}
                    </TabsTrigger>
                  </span>
                </TooltipTrigger>
                {!validacionCompleta && (
                  <TooltipContent>
                    <p>{t("muestras.atencion.completar_validacion_primero")}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </TabsList>

          {/* Tab: Encuesta */}
          <TabsContent value="encuesta">
            <EncuestaPreanaliticaPanel
              atencionId={atencionId}
              onComplete={() => setTabActivo("validacion")}
            />
          </TabsContent>

          {/* Tab: Validación */}
          <TabsContent value="validacion">
            <ValidacionPanel
              atencion={atencion}
              onValidar={() => validarMutation.mutate()}
              isValidating={validarMutation.isPending}
              onContinuar={() => setTabActivo("muestras")}
            />
          </TabsContent>

          {/* Tab: Muestras */}
          <TabsContent value="muestras">
            <MuestrasPanel
              atencion={atencion}
              onGenerarMuestras={() => generarMuestrasMutation.mutate()}
              isGenerando={generarMuestrasMutation.isPending}
            />
          </TabsContent>
        </Tabs>

        {/* Botón de cierre */}
        {estaAbierta && (
          <div className="mt-6 flex justify-end">
            <Button variant="default" onClick={() => setIsCierreDialogOpen(true)}>
              {t("muestras.cierre.cerrar_atencion")}
            </Button>
          </div>
        )}
      </div>

      {/* Dialog de cierre */}
      <CierreAtencionDialog
        open={isCierreDialogOpen}
        onOpenChange={setIsCierreDialogOpen}
        atencionId={atencionId}
        onConfirm={(data) => cerrarMutation.mutate(data)}
        isLoading={cerrarMutation.isPending}
      />
    </div>
  );
}
